/**
 * Signature capture (signature_pad) for both the office screen and the iPad. The canvas is
 * sized to the device pixel ratio so strokes stay sharp, and painted white so the PNG is
 * readable on any background.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import SignaturePad from 'signature_pad';

export interface SignatureHandle {
  isEmpty(): boolean;
  clear(): void;
  toDataURL(): string;
  toBlob(): Promise<Blob>;
}

export const SignatureBox = forwardRef<SignatureHandle, { height?: number; onChange?: (empty: boolean) => void; className?: string }>(function SignatureBox(
  { height = 220, onChange, className = '' },
  ref,
) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pad = useRef<SignaturePad | null>(null);
  const changed = useRef(onChange);
  changed.current = onChange;

  useEffect(() => {
    const c = canvas.current!;
    const p = new SignaturePad(c, { backgroundColor: 'rgb(255,255,255)', penColor: 'rgb(0,0,0)', minWidth: 1, maxWidth: 3 });
    pad.current = p;
    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      c.width = c.offsetWidth * ratio;
      c.height = c.offsetHeight * ratio;
      c.getContext('2d')!.scale(ratio, ratio);
      p.clear(); // resizing wipes the canvas; start clean rather than show a distorted signature
      changed.current?.(true);
    };
    resize();
    const end = () => changed.current?.(p.isEmpty());
    p.addEventListener('endStroke', end);
    window.addEventListener('resize', resize);
    return () => {
      p.removeEventListener('endStroke', end);
      window.removeEventListener('resize', resize);
      p.off();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    isEmpty: () => pad.current?.isEmpty() ?? true,
    clear: () => {
      pad.current?.clear();
      changed.current?.(true);
    },
    toDataURL: () => pad.current!.toDataURL('image/png'),
    toBlob: () => new Promise<Blob>((resolve, reject) => canvas.current!.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not read the signature'))), 'image/png')),
  }));

  return (
    <canvas
      ref={canvas}
      style={{ height, touchAction: 'none' }}
      className={`w-full rounded-lg border-2 border-dashed border-slate-400 bg-white ${className}`}
      aria-label="Sign here"
    />
  );
});
